
import ApiClient from './client';
import axios from 'axios';

const path = require('path');


class LoginAPI extends ApiClient {
  
  logged = false;
  token = null;
  userData = null;

  constructor() {
    super();
  }

  async signUp(data) {
    const hasUser = data.user_id;
    let response;
    let expected_status;
    if (hasUser) {
      const url = path.join(`${this.LOGIN_API_PATH}`, `/signup/`, data.user_id, '/');
      response = await this.patch(url, data);
      expected_status = 200;
    }
    else {
      const url = path.join(`${this.LOGIN_API_PATH}`, `/signup/`);
      response = await this.post(url, data);
      expected_status = 201;
    }

    if (response.status === expected_status) {
      return response.data;
    } else {
      console.error(response);
      return false;
    }
  }

  async session() {
    const url = path.join(`${this.LOGIN_API_PATH}`, `/session/`);
    const response = await this.get(url);
    if (response.status === 200 && response.data.authenticated) {
      this.logged = true;
      this.token = response.data.user.token;
      return response.data;
    } else {
      this.logged = false;
      this.token = null;
      console.error(response);
      return false;
    }
  }

  async signIn(data) {
    const url = path.join(`${this.LOGIN_API_PATH}`, `/signin/`);
    const response = await this.post(url, data);
    if (response.status === 200) {
      this.logged = true;
      this.token = response.data.token;
      return response.data;
    } else {
      this.logged = false;
      this.token = null;
      console.error(response);
      return false;
    }
  }

  async globalSignOut() {
    const EXTERNAL_LOGOUT_URL = process.env.VUE_APP_EXTERNAL_LOGOUT_URL;
    const EXTERNAL_LOGOUT_METHOD = process.env.VUE_APP_EXTERNAL_LOGOUT_METHOD ? process.env.VUE_APP_EXTERNAL_LOGOUT_METHOD.toLowerCase() : 'get';

    const logoutPromises = [
      this.signOut(), // This will reject if sign-out fails
    ];

    if (EXTERNAL_LOGOUT_URL) {
      if (EXTERNAL_LOGOUT_METHOD === 'get') {
        logoutPromises.push(new Promise(resolve => {
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = EXTERNAL_LOGOUT_URL;

          const timeoutId = setTimeout(() => {
            if (iframe.parentNode) {
              document.body.removeChild(iframe);
            }
            resolve();
          }, 3000); // Give it 3 seconds to load/send request

          iframe.onload = () => {
            clearTimeout(timeoutId);
            if (iframe.parentNode) {
              document.body.removeChild(iframe);
            }
            resolve();
          };
          iframe.onerror = () => {
            clearTimeout(timeoutId);
            if (iframe.parentNode) {
              document.body.removeChild(iframe);
            }
            console.warn("Iframe external logout onerror triggered for GET request, but request likely sent.");
            resolve();
          };

          document.body.appendChild(iframe);
        }));
      } else if (EXTERNAL_LOGOUT_METHOD === 'post') {
        // For POST, iframe src cannot be used, revert to axios, but warn about CORS
        console.warn(`External logout URL (${EXTERNAL_LOGOUT_URL}) is configured with POST method. This may lead to CORS issues if the server does not allow cross-origin POST requests. The request will be attempted via axios.`);
        logoutPromises.push(axios[EXTERNAL_LOGOUT_METHOD](EXTERNAL_LOGOUT_URL));
      } else {
        console.warn(`Unsupported EXTERNAL_LOGOUT_METHOD: ${EXTERNAL_LOGOUT_METHOD}. Defaulting to axios GET for external logout.`);
        logoutPromises.push(axios.get(EXTERNAL_LOGOUT_URL));
      }
    } else {
      logoutPromises.push(Promise.resolve()); // No external URL, just resolve
    }

    const results = await Promise.allSettled(logoutPromises);

    // Log any rejections from individual logout processes
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        if (index === 0) {
          console.error("Internal signOut failed:", result.reason);
        } else {
          console.error("External logout failed:", result.reason);
        }
      }
    });

    // Always clear local session state after attempting all logouts
    this.logged = false;
    this.token = null;

    // Return a resolved promise since SignOut.vue expects globalSignOut().then(...) to run.
    return Promise.resolve();
  }

  async signOut(data) {
    const url = path.join(`${this.LOGIN_API_PATH}`, `/signout/`);
    const response = await this.get(url, data);
    if (response.status === 200) {
      this.logged = false;
      this.token = null;
      return response.data;
    } else {
      console.error(response);
      throw new Error('Sign out failed'); // Reject the promise on error
    }
  }

  async validationRegistration(data) {
    const url = path.join(`${this.LOGIN_API_PATH}`, `/signup-confirmation/`);
    const response = await this.post(url, data);
    if (response.status === 200) {
      return true;
    } else {
      console.error(response);
      return false;
    }
  }

  async getUserDetail() {
    const url = path.join(`${this.LOGIN_API_PATH}`, `/user/`);
    const response = await this.get(url);
    if (response.status === 200) {
      this.userData = response.data;
      return response.data;
    } else {
      console.error(response);
      this.userData = null;
      return false;
    }
  }

  async updateUserDetail(data) {
    const url = path.join(`${this.LOGIN_API_PATH}`, `/user/`);
    const response = await this.put(url, data);
    if (response.status === 200) {
      return response.data;
    } else {
      console.error(response);
      return false;
    }
  }

  async deleteUser() {
    const url = path.join(`${this.LOGIN_API_PATH}`, '/user/');
    const response = await this.delete(url);
    if (response.status === 200) {
      return response.data;
    } else {
      console.error(response);
      return false;
    }
  }

  async forgottenPasswordRequest(data) {
    const url = path.join(`${this.LOGIN_API_PATH}`, `/forgotten-password-request/`);
    try {
      const response = await this.post(url, data);
      if (response.status === 200) {
        return response;
      }
    } catch (err) {
      return err.response;
    }
  }

  async forgottenPasswordConfirm(data) {
    const url = path.join(`${this.LOGIN_API_PATH}`, `/forgotten-password-confirmation/`);
    try {
      const response = await this.post(url, data);
      if (response.status === 200) {
        return response;
      }
    } catch (err) {
      return err.response;
    }
  }

  async newEmailConfirm(data) {
    const url = path.join(`${this.LOGIN_API_PATH}`, `/email-update-confirmation/`);
    try {
      const response = await this.post(url, data);
      if (response.status === 200) {
        return true;
      } else {
        return false;
      }
    } catch {
      return false;
    }
  }

  async getTermsOfUse() {
    const url = path.join(`${this.LOGIN_API_PATH}`, `/term-of-use/`);
    try {
      const response = await this.get(url);
      if (response.status === 200) {
        return response;
      }
    } catch (err) {
      return err.response;
    }
  }

  async postTermsOfUseAgreement(data) {
    const url = path.join(`${this.LOGIN_API_PATH}`, `/term-of-use/`);
    try {
      const response = await this.post(url, data);
      if (response.status === 200) {
        return response;
      }
    } catch (err) {
      return err.response;
    }
  }

  async signInWithToken(data) {
    const url = path.join(`${this.LOGIN_API_PATH}`, `/signin-withtoken/`);
    try {
      const response = await this.post(url, data);
      if (response.status === 200) {
        this.logged = true;
        this.token = response.data.token;
        return response.data;
      }
    } catch (err) {
      this.logged = false;
      this.token = null;
      return err.response;
    }
  }
};
const loginAPI = new LoginAPI();
export default loginAPI;
